import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Prompt } from "react-router-dom";
import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { Modal } from "components/designV2/modal/modal";
import { SidePanel } from "components/designV2/sidePanel/sidePanel";
import { useCloseOnEscape } from "hooks/useCloseOnEscape";
import type { AddStaffPanelProps } from "./addStaffPanel.contract";
import { AddStaffForm } from "./addStaffForm.component";
import { useAddStaffFormLogic } from "./addStaffForm.logic";
import { FooterWrapper, SubmitErrorText } from "./addStaffPanel.styled";

const PANEL_TITLE_ID = "add-staff-panel-title";

export const AddStaffPanel = (
  props: AddStaffPanelProps,
): JSX.Element | null => {
  const { t } = useTranslation();
  const onClose = props.onClose;
  const onSaved = props.onSaved;
  const [isDiscardConfirmOpen, setIsDiscardConfirmOpen] = useState(false);

  const handleSaved = useCallback((): void => {
    onSaved();
    onClose();
  }, [onClose, onSaved]);

  const logic = useAddStaffFormLogic({
    initialFormData:
      props.mode === "newEmployment" ? props.initialFormData : undefined,
    isOpen: props.isOpen,
    mode: props.mode,
    t,
    unitTeamOptions: props.unitTeamOptions,
    workerId: props.mode === "newEmployment" ? props.workerId : null,
    onSaved: handleSaved,
  });
  const isSubmitting = logic.isSubmitting;

  useEffect(() => {
    if (props.isOpen) return;
    setIsDiscardConfirmOpen(false);
  }, [props.isOpen]);

  const closePanel = useCallback((): void => {
    setIsDiscardConfirmOpen(false);
    onClose();
  }, [onClose]);

  const requestClose = useCallback((): void => {
    if (isSubmitting) return;
    if (logic.isFormStarted) {
      setIsDiscardConfirmOpen(true);
      return;
    }
    closePanel();
  }, [closePanel, isSubmitting, logic.isFormStarted]);

  useEffect(() => {
    if (!props.isOpen || !logic.isFormStarted) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent): void => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [props.isOpen, logic.isFormStarted]);

  useCloseOnEscape({
    isOpen: props.isOpen && !isSubmitting && !isDiscardConfirmOpen,
    onClose: requestClose,
    stopPropagation: true,
    preventDefault: true,
  });

  if (!props.isOpen) return null;

  return (
    <>
      <Prompt
        when={logic.isFormStarted}
        message={t("ProfileCompany.YouHaveUnSavedChanges")}
      />
      <SidePanel
        isOpen={true}
        onClose={requestClose}
        onBackdropClick={requestClose}
        title={t("Staff.AddStaffPanelTitle")}
        titleId={PANEL_TITLE_ID}
        footer={
          <FooterWrapper>
            {logic.submitError.length > 0 && (
              <SubmitErrorText role="alert">
                {logic.submitError}
              </SubmitErrorText>
            )}
            <PrimaryButton
              label={t("General.Save")}
              disabled={logic.isCreateDisabled}
              isLoading={logic.isSubmitting}
              onClick={logic.onSubmitClick}
            />
          </FooterWrapper>
        }
      >
        <AddStaffForm form={logic} />
      </SidePanel>
      <Modal
        variant="confirm"
        label={t("Staff.AddStaffPanelDiscardConfirmTitle")}
        size="compact"
        showCloseButton={false}
        isOpen={isDiscardConfirmOpen}
        onClose={() => setIsDiscardConfirmOpen(false)}
        footer={
          <>
            <SecondaryButton
              label={t("General.Cancel")}
              onClick={() => setIsDiscardConfirmOpen(false)}
            />
            <PrimaryButton
              label={t("Staff.AddStaffPanelDiscardConfirmAction")}
              onClick={closePanel}
            />
          </>
        }
      >
        {t("Staff.AddStaffPanelDiscardConfirmBody")}
      </Modal>
    </>
  );
};
